import React from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Paper,
  Stack,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import { GuidelineDocDetail as GuidelineDoc, GuidelineChunk } from "../../api";
import { StatCard } from "../../components/shared";
import { ColorsEnum, hexToRGBA } from "../../common/theme/colors";
import ChunksList from "./ChunksList";
import * as S from "./style";
import { storageUriToFullUrl } from "../../utils/storage";

interface GuidelineDetailPageProps {
  guideline: GuidelineDoc | null;
  loading: boolean;
  onBack: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onRefresh?: () => void;
  onChunkUpdated?: () => void;
}

const GuidelineDetailPage: React.FC<GuidelineDetailPageProps> = ({
  guideline,
  loading,
  onBack,
  onEdit,
  onDelete,
  onRefresh,
  onChunkUpdated,
}) => {
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 8,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!guideline) {
    return (
      <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Guideline not found.
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          sx={{
            borderColor: ColorsEnum.coolgray5,
          }}
        >
          Back to Guidelines
        </Button>
      </Paper>
    );
  }

  const chunks: GuidelineChunk[] = guideline.chunks || [];
  // `embed_status` indicates a chunk has been embedded; `to_embed` marks chunks
  // that should be enqueued for embedding. Count embedded/pending accordingly.
  const embeddedCount = chunks.filter((c) => c.embed_status != null).length;
  const pendingCount = chunks.filter((c) => c.to_embed === true && (c.embed_status == null)).length;

  return (
    <Box>
      {/* Header with Action Buttons */}
      <Box sx={{ 
        ...S.StyledBackButtonBox, 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "center" 
      }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          sx={{
            borderColor: ColorsEnum.coolgray5,
          }}
        >
          Back
        </Button>
        <Box sx={{ display: "flex", gap: 1 }}>
          {onRefresh && (
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={onRefresh}
              sx={{
                borderColor: ColorsEnum.coolgray5,
              }}
            >
              Refresh
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={onEdit}
            sx={{
              borderColor: ColorsEnum.coolgray5,
            }}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            startIcon={<DeleteIcon />}
            onClick={onDelete}
            sx={{
              color: ColorsEnum.error,
              borderColor: ColorsEnum.error,
              "&:hover": {
                borderColor: ColorsEnum.error,
                bgcolor: hexToRGBA(ColorsEnum.error, 0.04),
              },
            }}
          >
            Delete
          </Button>
        </Box>
      </Box>

      {/* Metadata Card */}
      <S.StyledMetadataPaper elevation={1}>
        <Box sx={S.StyledMetadataHeader}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {guideline.image_url && (
              <img
                src={storageUriToFullUrl(guideline.image_url) || undefined}
                alt="logo"
                style={{
                  width: S.StyledLogo.width,
                  height: S.StyledLogo.height,
                  borderRadius: S.StyledLogo.borderRadius,
                  objectFit: S.StyledLogo.objectFit,
                }}
              />
            )}
            <Typography variant="h6" fontWeight={600}>
              {guideline.title}
            </Typography>
          </Box>
        </Box>
        <Stack spacing={1} sx={S.StyledMetadataContent}>
          <Typography variant="body2">
            <strong>Description:</strong> {guideline.description || "—"}
          </Typography>
          <Typography variant="body2">
            <strong>Source Org:</strong> {guideline.source_org || "—"}
          </Typography>
          <Typography variant="body2">
            <strong>Author:</strong> {guideline.author || "—"}
          </Typography>
          <Typography variant="body2">
            <strong>Language:</strong> {guideline.language || "—"}
          </Typography>
          <Typography variant="body2">
            <strong>Version:</strong> {guideline.version_label || "—"}
          </Typography>
          <Typography variant="body2">
            <strong>Jurisdiction:</strong> {guideline.jurisdiction || "—"}
          </Typography>
          {guideline.url && (
            <Typography variant="body2">
              <strong>URL:</strong>{" "}
              <a href={guideline.url} target="_blank" rel="noopener noreferrer">
                {guideline.url}
              </a>
            </Typography>
          )}
          <Typography variant="body2">
            <strong>Published:</strong>{" "}
            {guideline.published_date
              ? new Date(guideline.published_date).toLocaleDateString()
              : "—"}
          </Typography>
          <Typography variant="body2">
            <strong>Created:</strong>{" "}
            {guideline.created_at
              ? new Date(guideline.created_at).toLocaleString()
              : "—"}
          </Typography>
        </Stack>
      </S.StyledMetadataPaper>

      {/* Embedding Stats */}
      <Box sx={S.StyledStatsGrid}>
        <StatCard label="Total Chunks" value={chunks.length} />
        <StatCard label="Embedded" value={embeddedCount} />
        <StatCard label="Pending" value={pendingCount} />
      </Box>

      {/* Chunk List */}
      <ChunksList chunks={chunks} guidelineId={guideline.guideline_docs_id} onChunkUpdated={onChunkUpdated} />
    </Box>
  );
};

export default GuidelineDetailPage;
